import * as React from "react";
import { Grid, Paper, CircularProgress, Box, Button, Chip } from "@mui/material";
import Typography from "@mui/material/Typography";
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import EditIcon from '@mui/icons-material/Edit';
import { useQuery } from '@tanstack/react-query';
import { BASE_API_URL } from "../../Config";
import axios from "axios";
import useAuthentication from "../../service/AuthenticationService";
import { useDialplanNodeContext } from "./DialplanNode";

const instance = axios.create({
  timeout: 30_000,
  headers: {
    "Content-Type": "application/json",
  }
});

function usePhones() {
  const authentication = useAuthentication();

  return useQuery({
    queryKey: ['phones'],
    queryFn: async () => {
      const response = await instance.get(
        `${BASE_API_URL}/phones`,
        {
          headers: {
            "Authorization": "Bearer " + authentication.token
          }
        }
      );
      return response.data;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!authentication.token,
  });
}

function DialplanRedirect() {
  const { node } = useDialplanNodeContext();
  const { data: phones, isLoading } = usePhones();
  const targetPhoneUuid = node?.phoneUuid;

  // Find the target phone
  const targetPhone = phones?.find(phone => phone.uuid === targetPhoneUuid);

  // Build URL to edit the target phone's dialplan
  const buildEditUrl = () => {
    const params = new URLSearchParams(window.location.search);
    params.set('phoneUuid', targetPhoneUuid);
    return `${window.location.pathname}?${params}`;
  };

  return (
    <Grid container direction={"column"}>
      <Grid item xs={12}>
        <div style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 8
        }}>
          <span style={{ fontSize: '1.5rem' }}>🔀</span>
          <Typography variant="h6" align="left">
            Usa il piano di risposta di un altro numero

          </Typography>
        </div>
      </Grid>

      <Grid item xs={12} sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Questo numero utilizza la segreteria/risposta già configurata per un altro numero del tuo centralino.
        </Typography>
      </Grid>

      <Grid container sx={{ mt: 2 }}>
        <Grid item xs={12}>
          {isLoading ? (
            <Paper elevation={1} sx={{
              backgroundColor: '#f5f5f5',
              padding: 2,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}>
              <CircularProgress size={20} />
              <Typography variant="body2">
                Caricamento...
              </Typography>
            </Paper>
          ) : targetPhone ? (
            <Paper elevation={2} sx={{
              background: 'linear-gradient(135deg, #e3f2fd 0%, #f1f8ff 100%)',
              padding: 3,
              borderRadius: 3,
              border: '1px solid #64b5f6',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: 'linear-gradient(90deg, #2196f3, #64b5f6)'
              }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  backgroundColor: '#2196f3',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <SettingsSuggestIcon sx={{ color: 'white', fontSize: '1.2rem' }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{
                    fontWeight: 600,
                    color: '#1565c0',
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                    letterSpacing: '0.5px'
                  }}>
                    Piano di risposta condiviso
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center', 
                gap: 1.5,
                mb: 2,
                padding: 3,
                backgroundColor: 'rgba(33, 150, 243, 0.08)',
                borderRadius: 2,
                border: '1px dashed #64b5f6',
                textAlign: 'center'
              }}>
                <Typography variant="body2" sx={{
                  color: '#0d47a1',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  lineHeight: 1.5
                }}>
                  Le chiamate saranno risposte con il piano di risposta di:
                </Typography>
                <ArrowDownwardIcon sx={{ color: '#1976d2', fontSize: '1.5rem' }} />
                <Box>
                  <Typography variant="subtitle1" sx={{
                    color: '#0d47a1',
                    fontWeight: 700,
                    fontSize: '1.1rem'
                  }}>
                    {targetPhone.name || targetPhone.phoneNumber}
                  </Typography>
                  {targetPhone.name && (
                    <Typography variant="body2" sx={{
                      color: '#1565c0',
                      fontSize: '0.75rem',
                      mt: 0.5
                    }}>
                      {targetPhone.phoneNumber}
                    </Typography>
                  )}
                </Box>
              </Box>
              
              <Button
                href={buildEditUrl()}
                variant="contained"
                startIcon={<EditIcon />}
                fullWidth
                sx={{
                  backgroundColor: '#1976d2',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  borderRadius: 2,
                  padding: '10px 20px',
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    backgroundColor: '#1565c0',
                    boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                    transform: 'translateY(-2px)'
                  },
                  '&:active': {
                    transform: 'translateY(0)'
                  }
                }}
              >
                Modifica la risposta di {targetPhone.name || targetPhone.phoneNumber}
              </Button>
            </Paper>
          ) : (
            <Paper elevation={1} sx={{
              backgroundColor: '#fff3e0',
              padding: 2,
              borderRadius: 2,
              border: '1px solid #ff9800'
            }}>
              <Typography variant="body2" sx={{
                color: '#e65100'
              }}>
                ⚠️ Numero di destinazione non trovato
              </Typography>
            </Paper>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
}

DialplanRedirect.description = "Reindirizza la chiamata al dialplan di un altro tuo numero";
DialplanRedirect.title = "Reindirizza ad altro numero";

// Not editable, not selectable - only for display
DialplanRedirect.isAdvanced = true;
DialplanRedirect.hideFromPicker = true; // Custom flag to hide from node picker

export default DialplanRedirect;
